import classNames from 'clsx';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { familyFeaturePreviewAlbumsTooltip } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { useFamilyShowPreviewHook } from 'reactApp/hooks/useFamilyShowHook';
import { createAlbumRequest, loadAlbumsListRequest } from 'reactApp/modules/albums/albums.actions';
import { CUSTOM_ALBUMS_ID } from 'reactApp/modules/albums/albums.constants';
import {
    getCustomAlbumsErrorState,
    getCustomAlbumsList,
    getCustomAlbumsLoadingState,
    getSharedAlbums,
} from 'reactApp/modules/albums/albums.selector';
import type { IAlbum } from 'reactApp/modules/albums/albums.types';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature, getFamilyLoadingState } from 'reactApp/modules/family/family.selectors';
import { familyContentLoadRequest } from 'reactApp/modules/familyContent/familyContent.module';
import { getFamilyContentAlbumsList } from 'reactApp/modules/familyContent/familyContent.selectors';
import { FamilyContentCategoryType } from 'reactApp/modules/familyContent/familyContent.types';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { FamilyPreviewPage } from 'reactApp/sections/FamilyPreviewPage/FamilyPreviewPage';
import { AlbumsGrid } from 'reactApp/ui/AlbumsGrid/AlbumsGrid';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { PageTabMenu } from 'reactApp/ui/PageTabMenu/PageTabMenu';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { FamilyTooltip } from '../FamilyPage/FamilyTooltip/FamilyTooltip';
import { PersonalAlbumsPageTabs as CustomAlbumsPageTabs } from './AlbumsPage.types';
import styles from './CustomAlbumsPage.css';
import { setShowFamilyPageOrAlbums } from './CustomAlbumsPage.helpers';

export const personalAlbumsPageTabs = [
    {
        name: 'Все',
        value: CustomAlbumsPageTabs.all,
    },
    {
        name: 'Общие',
        value: CustomAlbumsPageTabs.shared,
    },
];

export const CustomAlbumsPage = memo(function CustomAlbumsPage() {
    const dispatch = useDispatch();
    const [currentTab, setCurrentTab] = useState(CustomAlbumsPageTabs.all);
    const [tabs, setTabs] = useState(personalAlbumsPageTabs);
    const { isLoaded } = useSelector(getFamilyLoadingState);

    const hasFamilyFoldersFeature = useSelector(getFamilyFoldersFeature);
    const hasFamilyAlbumsFeature = useSelector(getFamilyAlbumsFeature);
    const showFeature = hasFamilyFoldersFeature && hasFamilyAlbumsFeature;
    const isFamilyCreate = currentTab === CustomAlbumsPageTabs.family && isLoaded && showFeature;
    const showFamilyPreview = useFamilyShowPreviewHook();
    const familyAlbumsTooltipRef = useRef<HTMLAnchorElement>(null);
    const showFamilyAlbumsTooltip = useSelector(PromoSelectors.pullPromo(EPromoType.showFamilyAlbumsTooltip));

    useEffect(() => {
        dispatch(
            routeChangeSuccess({ id: CUSTOM_ALBUMS_ID, storage: EStorageType.albums, params: {}, __isFolder: true, url: '', __parent: '' })
        );
        dispatch(loadAlbumsListRequest());
    }, []);

    useEffect(() => {
        if ((isLoaded && showFeature) || (showFamilyPreview && !tabs.find((item) => item.value === CustomAlbumsPageTabs.family))) {
            setTabs([
                ...tabs,
                {
                    name: 'Семейные',
                    value: CustomAlbumsPageTabs.family,
                },
            ]);

            if (showFeature) {
                dispatch(familyContentLoadRequest({ category: FamilyContentCategoryType.albums }));
            }
        }
    }, [isLoaded, showFeature, showFamilyPreview]);

    useEffect(() => {
        if (currentTab === CustomAlbumsPageTabs.family) {
            sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'albums-all', source: 'family-albums' });
        }

        if (currentTab === CustomAlbumsPageTabs.family && familyFeaturePreviewAlbumsTooltip) {
            dispatch(setShowFamilyPageOrAlbums());
        }
    }, [currentTab]);

    function handleTabsClick(selectedTab: CustomAlbumsPageTabs) {
        if (selectedTab === CustomAlbumsPageTabs.family) {
            showFamilyAlbumsTooltip?.onClick();
        }
        setCurrentTab(selectedTab);
    }

    function hadndleAlbumCreate() {
        dispatch(createAlbumRequest());
    }

    const onCreateFamilyAlbum = (): void => {
        toolbarActions.create('familyAlbum', 'emptyState', undefined, '', true, true);
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'album-click', type_album: 'family-album' });
    };

    const isLoading = useSelector(getCustomAlbumsLoadingState);
    const isError = useSelector(getCustomAlbumsErrorState);

    const albums = useSelector(getCustomAlbumsList);
    const shared = useSelector(getSharedAlbums);
    const familyAlbums = useSelector(getFamilyContentAlbumsList);

    let list: IAlbum[] = [];
    switch (currentTab) {
        case CustomAlbumsPageTabs.all:
            list = albums;
            break;
        case CustomAlbumsPageTabs.shared:
            list = shared;
            break;
        case CustomAlbumsPageTabs.family:
            list = familyAlbums;
            break;
    }

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    <BreadcrumbsContainer />
                    <PageTabMenu
                        current={currentTab}
                        tabRefs={{ [CustomAlbumsPageTabs.family]: familyAlbumsTooltipRef }}
                        tabs={tabs}
                        onClick={handleTabsClick}
                    />
                </div>

                {familyAlbumsTooltipRef.current !== null && showFamilyAlbumsTooltip && (
                    <FamilyTooltip
                        target={familyAlbumsTooltipRef}
                        buttonText="Хочу заглянуть"
                        title="Для тех, у кого есть семейная подписка"
                        text="Альбомами можно будет пользоваться вчетвером"
                        placement={ETooltipPlacement.rightStart}
                        onClose={showFamilyAlbumsTooltip.onClose}
                        onShow={showFamilyAlbumsTooltip.onShow}
                        onCrossClose={showFamilyAlbumsTooltip.onCrossClose}
                        onClick={() => {
                            showFamilyAlbumsTooltip.onClick();
                            setCurrentTab(CustomAlbumsPageTabs.family);
                        }}
                        qaId="family-tooltip-albums"
                    />
                )}

                <div className={styles.grid_container}>
                    {showFamilyPreview && currentTab === CustomAlbumsPageTabs.family ? (
                        <FamilyPreviewPage type={FamilyContentCategoryType.albums} paySource={`custom-albums-preview`} />
                    ) : (
                        <AlbumsGrid
                            handleCreateClick={isFamilyCreate ? onCreateFamilyAlbum : hadndleAlbumCreate}
                            createButtonText={isFamilyCreate ? 'Создать семейный\nальбом' : undefined}
                            showCreateButton={!showFamilyPreview}
                            albums={list}
                            isLoading={isLoading}
                            isError={isError}
                        />
                    )}
                </div>

                <Footer storage={EStorageType.albums} isAbsolutePos={true} />
            </div>
        </div>
    );
});
